import type { CSSProperties, ReactNode } from "react"

import {
  DetectionHeading,
  DetectionSummary,
} from "@/components/terminal/detection-body"
import { terminalAnchoredClassName } from "@/components/terminal/surfaces"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  dataByDetectionClass,
  type DetectionClass,
} from "@/constants/terminal/detection-class"
import type { TerminalDetectionView } from "@/lib/terminal/file-view"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

const detectionColorStyle = (detectionClass: DetectionClass) =>
  ({
    "--terminal-detection-color": dataByDetectionClass[detectionClass].color,
  }) as CSSProperties

// The one way a detection is revealed in place (Dhanur, 26 Oct 2026): the
// detection as its rail card reads opened, edged in its class colour like
// the rail's lane. A reassessment line opens it from its underlined type
// (`children`, the trigger). A picture's mark opens it instead through
// `hold`: the mark's box as the anchor, opened and closed by the picture
// (on hover, held on click; it mounts the popover only while it is open),
// and, opened by a hover, it takes no focus.
export const DetectionPopover = ({
  detection,
  children,
  hold,
}: {
  detection: TerminalDetectionView
  children?: ReactNode
  hold?: {
    anchor: { getBoundingClientRect: () => DOMRect }
    onOpenChange: (isOpen: boolean) => void
    // The pointer entering and leaving the card, so a hover can travel from
    // the mark onto it (to a source link) without closing it.
    onPointerEnter?: () => void
    onPointerLeave?: () => void
  }
}) => {
  const { theme } = useTerminalTheme()
  return (
    <Popover open={hold ? true : undefined} onOpenChange={hold?.onOpenChange}>
      {hold ? (
        <PopoverAnchor virtualRef={{ current: hold.anchor }} />
      ) : (
        <PopoverTrigger className="inline-affordance cursor-pointer border-0 bg-transparent p-0 text-left text-inherit">
          {children}
        </PopoverTrigger>
      )}
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onOpenAutoFocus={hold && ((event) => event.preventDefault())}
        onPointerEnter={hold?.onPointerEnter}
        onPointerLeave={hold?.onPointerLeave}
        // Portaled to the body, outside the terminal theme scope.
        data-terminal-theme={theme}
        className={cn(
          theme === "dark" && "dark",
          terminalAnchoredClassName,
          "w-80 border-l-2 border-l-(--terminal-detection-color) p-3"
        )}
        style={detectionColorStyle(detection.class)}
      >
        <DetectionHeading detection={detection} />
        <div className="border-border mt-1.5 flex flex-col items-start gap-1.5 border-t pt-1.5">
          <DetectionSummary detection={detection} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
